import type { AnimationI, AnySceneObjectT, AttributeT, ImageI, SimpleStyleT } from "tweenly"
import {
    clearAllSelectedObjectsAction,
    clearObjectFromSelectedObjectsAction,
    setActiveObjectAction,
} from "../store/slices/activeObject.slice"

import * as tweenly from "tweenly"
import { Asset } from "tweenly/dist/fetchAsset"
import { v4 as uuidv4 } from "uuid"
import {
    addChildIdAction,
    addObjAction,
    checkIndexes,
    createImageObjectAction,
    delChildIdAction,
    delObjectAction,
    getAllDescendantIds,
    resizeCrawlTickerStylesAction,
    resizeScrollTickerStylesAction,
    updIndexAction,
    updObjectAction,
} from "../store/slices/objects.slice"
import { useAppDispatch, useAppStore } from "./useRedux"

import { createAnimation } from "tweenly"
import {
    defaultEllipse,
    defaultImage,
    defaultItem,
    defaultLine,
    defaultSequence,
    defaultText,
    defaultVideoPanel,
} from "../data/defaults/objects.types.defaults"
import { defaultEase } from "../data/styles/ease.styles"
import { lineHeight as lineHeightDefaultStyle } from "../data/styles/simple/font.styles"
import { sequence } from "../data/styles/tweens.styles"
import { FileResponse, postFile } from "../helpers/ajax.helpers"
import { PARENT_OBJECT_TYPES } from "../helpers/objectsTreeDrag.helpers"
import { S3_PREFIX, getImageMetadata } from "../helpers/file.helpers"
import { framesToSeconds } from "../helpers/framerate.helpers"
import { swapChildIds } from "../helpers/hooks.helpers"
import { createChildObject, createObject } from "../helpers/object.helpers"
import {
    selectCanvasResolution,
    selectCompanyId,
    selectUndoable,
} from "../helpers/selector.helpers"
import { useObjectGroupActions } from "../hooks/useObjectGroupActions"
import {
    addSelectedAnimationsAction,
    clearSelectedAnimationsAction,
    removeSelectedAnimationsAction,
    setSelectedAnimationsForObjectAction,
} from "../store/slices/activeAnimation.slice"
import { CreateAssetT, createAssetAsync } from "../store/slices/assets.slice"
import { clear } from "../store/slices/editor.slice"
import { setPercentLoading } from "../store/slices/loading.slice"
import { updateMasterTimeline } from "../store/slices/masterTimeline.slice"
import {
    filterAndSortSiblings,
    selectObjectById,
    selectObjects,
} from "../store/slices/objects.slice"
import { useAnimationActions } from "./useAnimationActions"
import { useTimelineActions } from "./useTimelineActions"

export const useObjectActions = () => {
    const { updateObjectGroups } = useObjectGroupActions()

    // This hook is instantiated in every CanvasItem, every AbstractContainer (through
    // useDraggable) and every ObjectsTreeNodeMenu — several hundred times on a large graphic.
    // It therefore subscribes to NOTHING: `useStore` reads state without subscribing, and the
    // nested hooks below were picked/reshaped so that none of them subscribes either. Anything
    // that has to be RENDERED belongs in the component instead — `videoPanelExists` moved to
    // ObjectsTreeActions, its only consumer.
    const store = useAppStore()

    const getTimeToSeek = () => store.getState().masterTimeline.time
    const getAssets = () => store.getState().assets.data
    const getGraphic = () => store.getState().graphic.data
    // memoized selector -> the same array reference until objects really change, so the
    // `createObject` wrapper in helpers/object.helpers keeps protecting it from the in-place
    // sort inside tweenly
    const getObjects = (): AnySceneObjectT[] =>
        selectObjects(selectUndoable(store.getState()).objects)

    const dispatch = useAppDispatch()

    const { addAnimations, createSequenceAnimations } = useAnimationActions()

    /* ACTIVE OBJECT HANDLERS */

    const setActiveObjectId = (id: string, animationsIds: string[]) => {
        dispatch(setActiveObjectAction({ id }))
        dispatch(setSelectedAnimationsForObjectAction({ animationsIds }))
    }

    const removeActiveObjectId = (object: AnySceneObjectT) => {
        dispatch(clearObjectFromSelectedObjectsAction({ id: object.id }))
        dispatch(removeSelectedAnimationsAction(Object.keys(object.animations)))
    }

    /* HELPER HANDLERS */

    const handleActiveObjectWhenCreating = (object: AnySceneObjectT) => {
        if (object && object.animations) {
            const animationIds = Object.keys(object.animations) ?? []
            setActiveObjectId(object.id, animationIds)
        } else {
            setActiveObjectId(object.id, [])
        }
    }

    const handleActiveObjectWhenDeleting = (object: AnySceneObjectT) => {
        const state = store.getState()
        const selectedObjectIds = state.activeObject.selected
        // pokud je mazany objekt akttivni
        if (selectedObjectIds.includes(object.id))
            // deaktivuj ho
            removeActiveObjectId(object)
        // jinak
        else {
            // najdi vsechny potomky
            const ids: string[] = getAllDescendantIds(selectUndoable(state).objects, object.id)
            // a pripadneho aktivniho potomka deaktivuj
            ids.forEach((id) => {
                if (selectedObjectIds.includes(object.id)) removeActiveObjectId(object)
            })
        }
        // clear object.animations from selectedAnimationsIds
        dispatch(removeSelectedAnimationsAction(Object.keys(object.animations)))
    }

    // const addToParentsChildIds = (object: AnySceneObjectT) => {
    const addToParentsChildIds = (parentId: string | null, objectId: string) => {
        // pokud ma novy objekt odkaz na rodice
        if (parentId)
            // pridej k rodici id na novy objekt, ktery je jeho potomkem
            dispatch(addChildIdAction({ id: parentId, childId: objectId }))
    }

    const deleteFromParentsChildIds = (object: AnySceneObjectT) => {
        // pokud je mazany objekt potomkem nektereho objektu a jeho rodic existuje
        if (
            object.parentId &&
            selectObjectById(selectUndoable(store.getState()).objects, object.parentId)
        )
            // smaz jeho referenci u daneho rodice
            dispatch(delChildIdAction({ id: object.parentId, childId: object.id }))
    }

    const isParentObjectType = (type: string): boolean => PARENT_OBJECT_TYPES.includes(type)

    /* MAIN HANDLERS */
    const addObject = (object: AnySceneObjectT): AnySceneObjectT | undefined => {
        if (object.parentId) {
            const parentobject = selectObjectById(
                selectUndoable(store.getState()).objects,
                object.parentId
            )
            if (!isParentObjectType(parentobject.type)) return undefined
        }
        dispatch(addObjAction({ object: object }))
        addToParentsChildIds(object.parentId, object.id)
        //clear all selected animations
        dispatch(clearSelectedAnimationsAction())
        handleActiveObjectWhenCreating(object)
        dispatch(updateMasterTimeline(getTimeToSeek()))
        return object
    }

    const objectTemplates = {
        item: defaultItem,
        ellipse: defaultEllipse,
        line: defaultLine,
        text: defaultText,
    }

    const addRootObject = (
        type: keyof typeof objectTemplates = "item"
    ): AnySceneObjectT | undefined =>
        addObject(createObject(getObjects(), objectTemplates[type], null))

    const addVideoPanel = (): AnySceneObjectT | undefined => {
        const { width, height } = selectCanvasResolution(store.getState())
        return addObject(defaultVideoPanel({ width, height }))
    }

    const addImage = (id?: string): AnySceneObjectT | undefined => {
        const object = createObject(getObjects(), defaultImage, null, id)
        return addObject(object)
    }
    const addSequence = (id?: string): AnySceneObjectT | undefined => {
        const object = createObject(getObjects(), defaultSequence, null, id)
        return addObject(object)
    }

    /** `addObject` already refuses a parent that cannot hold children. */
    const addChildObject = (
        parentId: string,
        type: keyof typeof objectTemplates = "item"
    ): AnySceneObjectT | undefined =>
        addObject(createChildObject(getObjects(), objectTemplates[type], parentId))

    const addChildImage = (parentId: string, type: "image" | "sequence", id?: string) => {
        const parentobject = selectObjectById(selectUndoable(store.getState()).objects, parentId)
        if (!isParentObjectType(parentobject.type)) return undefined

        return addObject(
            createChildObject(
                getObjects(),
                type === "image" ? defaultImage : defaultSequence,
                parentId,
                id
            )
        )
    }

    const duplicateObjectsAnimations = (objectId: string, sampleAnimations: AnimationI[]) => {
        const animations: AnimationI[] = sampleAnimations.map((sampleAnimation) => {
            return createAnimation(objectId, sampleAnimation.delay, sampleAnimation)
        })
        //to do refactoring addAnimation when duplicate objects
        if (animations.length > 0) addAnimations(animations)
        dispatch(updateMasterTimeline(getTimeToSeek()))
    }

    const updateObject = (id: string, property: any, value: any) => {
        dispatch(updObjectAction({ id: id, property: property, value: value }))
    }

    const updateObjectAttribute = (object: AnySceneObjectT, property: string, value: boolean) => {
        const attribute: AttributeT | undefined = object.attributes.find(
            (style) => style.property === property
        )
        if (!attribute) return

        const restAttributes: AttributeT[] = object.attributes.filter(
            (style) => style.property !== property
        )
        const updatedAttributes: AttributeT[] = [
            ...restAttributes,
            {
                ...attribute,
                value: value,
            },
        ]
        updateObject(object.id, "attributes", updatedAttributes)
    }

    const updateObjectStyle = (object: AnySceneObjectT, property: string | number, value: any) => {
        let style: SimpleStyleT | undefined = object.styles.find(
            (style) => style.property === property
        )
        if (!style && property === "lineHeight") {
            style = lineHeightDefaultStyle
        }
        if (!style) {
            return
        }
        if (style.value === value && style.property === property) {
            return
        } else {
            const restStyles: SimpleStyleT[] = object.styles.filter(
                (style) => style.property !== property
            )
            const updatedStyles: SimpleStyleT[] = [
                ...restStyles,
                {
                    ...style,
                    value: value,
                },
            ]
            updateObject(object.id, "styles", updatedStyles)
        }
    }

    const updateObjectStyles = (object: AnySceneObjectT, properties: Record<string, any>) => {
        let newStyles: SimpleStyleT[] = [...object.styles]

        Object.entries(properties).forEach(([property, value]) => {
            const style: SimpleStyleT | undefined = newStyles.find(
                (style) => style.property === property
            )
            if (style) {
                newStyles = newStyles.map((style) => {
                    if (style.property === property) {
                        return {
                            ...style,
                            value: value,
                        }
                    } else {
                        return style
                    }
                })
            }
        })

        if (object.type === "ticker") {
            if (object.tickerType.startsWith("crawl")) {
                dispatch(
                    resizeCrawlTickerStylesAction({
                        id: object.id,
                        property: "styles",
                        value: newStyles,
                    })
                )
            } else {
                dispatch(
                    resizeScrollTickerStylesAction({
                        id: object.id,
                        property: "styles",
                        value: newStyles,
                    })
                )
            }
        } else {
            updateObject(object.id, "styles", newStyles)
        }
    }

    const deleteObject = (object: AnySceneObjectT) => {
        handleActiveObjectWhenDeleting(object)
        deleteFromParentsChildIds(object)

        // smaz objekt
        dispatch(delObjectAction({ id: object.id }))
        dispatch(checkIndexes(object.parentId))
        dispatch(updateMasterTimeline(getTimeToSeek()))
    }

    const clearScene = () => {
        dispatch(clear())
    }

    const moveObjectUp = (object: AnySceneObjectT) => moveObject(object, -1)
    const moveObjectDown = (object: AnySceneObjectT) => moveObject(object, +1)

    const { refreshMasterTimeline } = useTimelineActions()

    const moveObject = (object: AnySceneObjectT, i: number) => {
        const objects = getObjects()

        const current = objects.find((candidate) => candidate.id === object.id)
        if (!current) return

        const siblings = filterAndSortSiblings(objects, current.parentId)
        const index = siblings.findIndex((sibling) => sibling.id === current.id) + i
        if (index >= 0 && index < siblings.length) {
            const siblingIds: string[] = siblings.map((s) => s.id)
            const sibling = siblings[index]

            if (current.parentId) {
                const childIds: string[] = swapChildIds(siblingIds, current.id, sibling.id)
                updateObject(current.parentId, "childIds", childIds)
            }
            // the two swap their indexes
            dispatch(updIndexAction({ id: current.id, index: sibling.index }))
            dispatch(updIndexAction({ id: sibling.id, index: current.index }))
        }
        //refresh timeline with animation after move up/down,
        //setTimeout because update index of object is async function
        setTimeout(() => refreshMasterTimeline(), 100)
    }

    const createSequence = (parentid: string | null) =>
        parentid ? addChildImage(parentid, "sequence") : addSequence()

    const setFillProperty = (object: ImageI, index: number, property: string, value: any) => {
        const updatedGroups = updateObjectGroups(object["fill"], index, property, value)
        updateObject(object.id, "fill", updatedGroups)
    }

    const processImage = (
        parentObjectId: string | null,
        url: string,
        attribute?: string,
        objectId?: string
    ) => {
        const item = createChildObject(getObjects(), defaultImage, parentObjectId, objectId)
        if (!item) {
            console.error("Cannot create an object")
            return
        }
        const img = new Image()
        img.addEventListener("load", function () {
            dispatch(
                createImageObjectAction(
                    {
                        object: item,
                        dimensions: { width: img.width, height: img.height },
                        fill: updateObjectGroups(
                            item["fill"],
                            0,
                            "value",
                            attribute ? attribute : `url("${img.src}")`
                        ),
                    },
                    selectNewObject
                )
            )
        })
        img.src = url
    }

    const selectNewObject = (id: string) => {
        dispatch(clearSelectedAnimationsAction())
        dispatch(clearAllSelectedObjectsAction())
        dispatch(setActiveObjectAction({ id: id }))
    }

    const readImageFile = async (file: File, parentObjectId: string | null) => {
        const item = createChildObject(getObjects(), defaultImage, parentObjectId)
        if (!item) {
            console.error("Cannot create an object")
            return
        }

        const img = new Image()
        img.addEventListener("load", function () {
            dispatch(
                createImageObjectAction(
                    {
                        object: item,
                        dimensions: { width: img.width, height: img.height },
                        fill: updateObjectGroups(item["fill"], 0, "value", `url("${img.src}")`),
                    },
                    selectNewObject
                )
            )
        })

        const graphicId = getGraphic()._id
        const fileMetadata = await postFile(file, {
            storage: "s3",
            key: `${S3_PREFIX}/${graphicId}/images/${item.id}`,
            isPublic: true,
            hasFilename: true,
        })
        img.src = fileMetadata.publicUrl!
        const companyId = selectCompanyId(store.getState())
        const asset: CreateAssetT = {
            name: file.name,
            slug: item.id,
            companyId,
            attributes: [],
            url: fileMetadata.publicUrl!,
            type: "image",
            size: fileMetadata.length,
            description: "",
        }
        dispatch(createAssetAsync(asset, companyId))
    }

    const readImageFileFromURL = async (
        url: string,
        parentObjectId: string | null,
        objectId?: string
    ) => {
        processImage(parentObjectId, url, undefined, objectId)
    }

    const readImageFileFromAsset = async (
        attributeImgUrl: string,
        parentObjectId: string | null
    ) => {
        const url = await tweenly.processField(attributeImgUrl, getAssets() as Asset[])
        processImage(parentObjectId, url!, attributeImgUrl)
    }

    const replaceImageSource = async (file: File, object: AnySceneObjectT) => {
        const graphicId = getGraphic()._id
        const fileMetadata = await postFile(file, {
            storage: "s3",
            key: `${S3_PREFIX}/${graphicId}/images/${object.id}`,
            isPublic: true,
            hasFilename: true,
        })
        const companyId = selectCompanyId(store.getState())
        const asset: CreateAssetT = {
            name: object.title,
            slug: object.id,
            companyId,
            attributes: [],
            url: fileMetadata.publicUrl!,
            type: "image",
            size: fileMetadata.length,
            description: "",
        }
        dispatch(createAssetAsync(asset, companyId))

        setFillProperty(object as ImageI, 0, "value", `url("${fileMetadata.publicUrl!}")`)
    }

    const replaceImageSourceFromUrl = async (url: string, object: AnySceneObjectT) => {
        setFillProperty(object as ImageI, 0, "value", `url("${url}")`)
    }
    const replaceImageSourceFromAsset = async (attribute: string, object: AnySceneObjectT) => {
        setFillProperty(object as ImageI, 0, "value", attribute)
    }

    const readImageSequence = async (files: FileList, parentObjectId: string | null) => {
        const step = framesToSeconds(store.getState().timelinePreferences.value.framerate, 1)

        const item = createChildObject(getObjects(), defaultSequence, parentObjectId)

        if (!item) {
            return
        }

        const graphicId = getGraphic()._id
        const { width } = await getImageMetadata(files[0])

        let length = 0
        const filesMetadata: FileResponse[] = []
        for (let i = 0; i < files.length; i++) {
            const metadata = await postFile(files[i], {
                storage: "s3",
                key: `${S3_PREFIX}/${graphicId}/sequences/${item.id}`,
                isPublic: true,
                thumbnailWidth: Math.round(width / 2),
                hasFilename: true,
            })
            length += metadata.length
            filesMetadata.push(metadata)
            dispatch(setPercentLoading((i / files.length) * 100))
        }

        const metadata = filesMetadata[Math.floor(files.length / 2)]
        const companyId = selectCompanyId(store.getState())
        const asset: CreateAssetT = {
            name: metadata.filename,
            slug: item.id,
            companyId,
            attributes: [],
            type: "sequence",
            size: length,
            url: metadata.publicUrl,
            urls: filesMetadata.map((file) => file.publicUrl!),
            description: "",
        }
        dispatch(createAssetAsync(asset, companyId))

        const animations = filesMetadata
            .sort((f1, f2) => f1.filename.localeCompare(f2.filename))
            .map((f, index) => {
                const animation: AnimationI = {
                    id: uuidv4(),
                    target: null,
                    title: f.filename,
                    objectId: item.id,
                    subTimeline: false,
                    duration: step,
                    delay: index * step,
                    repeat: 0,
                    tween: sequence(f.publicUrl!),
                    relativeTo: null,
                    ease: defaultEase,
                    onInitCode: null,
                    onUpdateCode: null,
                }
                return animation
            })

        const img = new Image()
        img.addEventListener("load", function () {
            createSequenceAnimations(animations, item, { width: img.width, height: img.height })
        })
        img.src = filesMetadata[0].publicUrl!
        const animationIds: string[] = animations.map((animation) => animation.id)
        dispatch(clearSelectedAnimationsAction())
        dispatch(clearAllSelectedObjectsAction())
        dispatch(setActiveObjectAction({ id: item.id }))
        dispatch(addSelectedAnimationsAction(animationIds))
    }

    const deselectObjects = () => {
        dispatch(clearSelectedAnimationsAction())
        dispatch(clearAllSelectedObjectsAction())
    }

    return {
        isParentObjectType,
        addRootObject,
        addImage,
        addObject,
        addChildObject,
        addChildImage,
        updateObject,
        updateObjectAttribute,
        updateObjectStyle,
        updateObjectStyles,
        deleteObject,
        clearScene,
        setActiveObjectId,
        removeActiveObjectId,
        moveObjectUp,
        moveObjectDown,
        addToParentsChildIds,
        duplicateObjectsAnimations,
        readImageFile,
        readImageFileFromURL,
        readImageFileFromAsset,
        readImageSequence,
        replaceImageSource,
        replaceImageSourceFromUrl,
        replaceImageSourceFromAsset,
        deselectObjects,
        createSequence,
        addVideoPanel,
    }
}
